/**
 * The out-of-shell auth screen: sign-in, `Unassigned`, and `deactivated`.
 *
 * Standalone -- outside the sidebar and header shell -- because in all three
 * states there is nothing to navigate to. Reproduces `signin.html` using the
 * f1.1 design tokens: every colour, radius and font resolves through a CSS
 * variable, and no brand hex appears below.
 *
 * **Which card renders is decided entirely by `access_state`, which the server
 * computed.** f1.2 branched on `authenticated` and a hardcoded role string;
 * f1.3 does not, for two reasons. A client that works out "may this person use
 * Atlas" from a role code is taking an authorization decision in a browser,
 * where it can be edited. And a role code cannot express `deactivated` at all:
 * a deactivated Platform Admin holds the most powerful role in the system and
 * must still be shown the door.
 *
 * **f17.3: Sign in with GFT, the Wynxx Discovery flow in Atlas's look.** The
 * decision order matters, because the sign-in card redirects to Microsoft on
 * its own:
 *
 *   1. the session is still loading -- nothing (App's guard);
 *   2. `deactivated` or `unassigned` -- their cards, which NEVER redirect, or
 *      the person would bounce through Microsoft and back forever;
 *   3. `?error=` or `?signed_out=1` -- the sign-in card, stopped: signing out
 *      must not sign straight back in, and a refusal must be read;
 *   4. otherwise -- redirect once to `/api/auth/sso?next=<path>`, with a
 *      visible link in case the redirect does not happen.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Info,
  LockKeyhole,
  LogOut,
  ShieldCheck,
  ShieldQuestion,
  UserX,
} from 'lucide-react';

import { signInHref } from '@/auth/signIn';
import { useSignOut } from '@/auth/useSignOut';
import { useSessionScope } from '@/session/SessionScopeContext';
import { BrandMark } from '@/shell/BrandMark';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Mark, name, sub-name -- the three lines that identify the application.
 *
 * On the sign-in card it is the whole of the card's head: there is no separate
 * "Sign in to Atlas" heading, because a heading directly under a wordmark that
 * already says `Atlas` said the name twice. The `Unassigned` and `deactivated`
 * cards open on the person's identity instead, so for those it stays above the
 * card where it introduces the page rather than the card.
 */
function BrandBlock() {
  return (
    <div className="auth-logo">
      <span className="auth-logo-mark">
        <BrandMark />
      </span>
      <div className="auth-logo-text">
        <div className="auth-wordmark">Atlas</div>
        <div className="hint">GFT US Delivery &amp; Governance Platform</div>
      </div>
    </div>
  );
}

/**
 * Why the API's sign-in sent the browser back here (`/api/auth/callback`). One
 * sentence each, saying what the person can do -- and since Sign in with GFT is
 * the only way in, never pointing at another one.
 */
export const SIGN_IN_ERRORS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled before it finished.',
  expired: 'The sign-in took too long to complete. Please try again.',
  deactivated:
    'This Atlas account has been deactivated. Only an Atlas administrator can reactivate it.',
  failed:
    'Sign-in could not be completed. Please try again later; if it keeps failing, contact an Atlas administrator.',
};

function SignInCard() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('error');
  const error = code ? (SIGN_IN_ERRORS[code] ?? SIGN_IN_ERRORS.failed) : null;
  const signedOut = params.get('signed_out') === '1';
  const href = signInHref(params.get('next'));
  const stopped = Boolean(error) || signedOut;

  // Once, even under StrictMode's double effect: one trip to Microsoft.
  const started = useRef(false);
  useEffect(() => {
    if (stopped || started.current) return;
    started.current = true;
    window.location.assign(href);
  }, [stopped, href]);

  return (
    <div className="card auth-card">
      <div className="card-body auth-signin-body">
        <BrandBlock />

        <div className="auth-divider" role="presentation" />

        {error ? (
          <div className="alert alert-warning" role="alert">
            <ShieldQuestion aria-hidden="true" />
            <div>
              <div className="a-title">You are not signed in</div>
              <div className="a-body">{error}</div>
            </div>
          </div>
        ) : null}
        {signedOut && !error ? (
          <div className="alert alert-info">
            <Info aria-hidden="true" />
            <div>
              <div className="a-body">You have signed out of Atlas.</div>
            </div>
          </div>
        ) : null}
        {!stopped ? (
          <p className="hint auth-status" role="status" aria-live="polite">
            Redirecting to GFT sign-in…
          </p>
        ) : null}

        <a className="btn btn-primary auth-action" href={href}>
          <ShieldCheck aria-hidden="true" />
          Sign in with GFT
        </a>
      </div>
    </div>
  );
}

/**
 * The identity strip both signed-in states share.
 *
 * The badge renders `role.name` straight from the payload. There is no map
 * from code to label anywhere in the frontend, which is what lets a role added
 * by an INSERT in phase 10 display correctly with no frontend change at all.
 */
function IdentityStrip({ badgeClass }: { badgeClass: string }) {
  const { user, role } = useSessionScope();

  const name = user?.display_name || 'Signed in';
  const email = user?.username ?? '';

  return (
    <div className="auth-identity">
      <span className="avatar" aria-hidden="true">
        {initialsOf(name)}
      </span>
      <div className="auth-identity-text">
        <div className="user-name">{name}</div>
        <div className="hint">{email}</div>
      </div>
      {role ? <span className={`badge ${badgeClass}`}>{role.name}</span> : null}
    </div>
  );
}

function SignOutFoot({ note }: { note: string }) {
  const signOut = useSignOut();
  const [signingOut, setSigningOut] = useState(false);

  return (
    <div className="card-foot">
      <span className="hint">{note}</span>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => {
          setSigningOut(true);
          void signOut();
        }}
        disabled={signingOut}
      >
        <LogOut aria-hidden="true" />
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

function UnassignedCard() {
  return (
    <div className="card auth-card">
      <div className="card-body">
        <IdentityStrip badgeClass="pill-pending" />

        <div className="empty auth-empty">
          <div className="e-icon">
            <LockKeyhole aria-hidden="true" />
          </div>
          <h3>Your account is not active yet</h3>
          <p>
            You are signed in, but no role has been assigned to you. An
            administrator has to grant you a role before any part of Atlas becomes
            visible.
          </p>
        </div>

        {/*
          The prototype promised "you will receive an e-mail once a role is
          granted". ADR-5 introduces no mail transport and no NotificationPort
          anywhere in Atlas, so that copy would promise a message that never
          arrives. It states what is actually true instead.
        */}
        <div className="alert alert-info">
          <Info aria-hidden="true" />
          <div>
            <div className="a-title">Your sign-in was recorded</div>
            <div className="a-body">
              Ask an Atlas administrator to grant you a role. They can see your
              account, so there is nothing further for you to submit.
            </div>
          </div>
        </div>
      </div>
      <SignOutFoot note="Signed in with your GFT account" />
    </div>
  );
}

/**
 * The deactivated state -- distinct from both sign-in and Unassigned.
 *
 * Signing in again cannot help, so offering the SSO button here would invite
 * a loop: Entra would authenticate the person perfectly well every time, and
 * Atlas would refuse them every time. The only honest action is to leave.
 *
 * This screen exists because `session` answers a deactivated caller (the one
 * stated exception to "denied everything"). Without that answer the SPA could
 * only show a sign-in screen to someone who is demonstrably signed in.
 */
function DeactivatedCard() {
  return (
    <div className="card auth-card">
      <div className="card-body">
        <IdentityStrip badgeClass="pill-deactivated" />

        <div className="empty auth-empty">
          <div className="e-icon e-icon-warning">
            <UserX aria-hidden="true" />
          </div>
          <h3>This account has been deactivated</h3>
          <p>
            Your Atlas account still exists and its history is intact, but it has
            been switched off. Signing in again will not restore access; only an
            administrator can reactivate the account.
          </p>
        </div>

        <div className="alert alert-warning">
          <Info aria-hidden="true" />
          <div>
            <div className="a-title">Nothing has been deleted</div>
            <div className="a-body">
              Atlas deactivates accounts rather than removing them, so everything
              you have recorded remains attributed to you. If you believe this is
              a mistake, contact an Atlas administrator.
            </div>
          </div>
        </div>
      </div>
      <SignOutFoot note="Access is suspended for this account" />
    </div>
  );
}

export function SignIn() {
  const { accessState } = useSessionScope();

  // One switch over the server's answer. No `authenticated &&`, no role-code
  // comparison, and no fall-through that could render the wrong card for a
  // state added later: an unrecognised value lands on sign-in, which is the
  // screen that grants nothing.
  // The sign-in card carries the brand inside it, so the page must not print
  // it a second time above.
  if (accessState === 'deactivated') {
    return (
      <div className="auth-page">
        <BrandBlock />
        <DeactivatedCard />
      </div>
    );
  }

  if (accessState === 'unassigned') {
    return (
      <div className="auth-page">
        <BrandBlock />
        <UnassignedCard />
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SignInCard />
    </div>
  );
}
